import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { applyLanguageFilteringSingle } from '@app/common/filtering-behavior';
import { HttpHelper } from '@app/common/http-helper';
import { NewsPage } from '@app/models/news-page';
import { Page } from '@app/models/page';
import { SearchPostQuery, SearchPostQueryRequest } from '@app/models/search-post-query';
import { UserChannelTerm } from '@app/models/user-channel-term';
import { UserLanguageTerm } from '@app/models/user-language-term';
import { CacheService } from '@app/services/cache.service';
import { CultureService } from '@app/services/culture.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { SearchService } from '@app/services/search.service';
import { SphomeService } from '@app/services/sphome.service';
import { Environment } from '@environments/interface.environment';
import { ListItem } from '@microsoft/microsoft-graph-types';
import { combineLatest, concat, forkJoin, iif, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, map, mergeMap, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { LicenseService } from './license.service';
import { UrlHelperService } from '@app/services/url-helper.service';
import { addMinutes } from 'date-fns';

@Injectable({
  providedIn: 'root'
})
export class NewsService {

  private readonly cacheKeyPrefix = 'NewsService';

  private readonly translationFields = '_SPIsTranslation,_SPTranslatedLanguages,_SPTranslationSourceItemId,_SPTranslationLanguage';

  public useCache = true;
  private getModernTopNewsGraph$ = new Map<string, Observable<Array<NewsPage>>>();
  private getTopNewsApi$ = new Map<string, Observable<Array<NewsPage>>>();
  private getModernPersonalNewsFromGraph$: Observable<Array<NewsPage>>;
  private getPersonalNewsFromApi$: Observable<Array<NewsPage>>;
  private getModernNewsFromSearch$ = new Map<string, Observable<Array<NewsPage>>>();

  public reloadAllTopNews$ = new Subject<void>();
  public reloadGetModernTopNewsGraph$ = new Subject<void>();
  public reloadGetTopNewsApi$ = new Subject<void>();
  public reloadAllPersonalNews$ = new Subject<void>();
  public reloadGetModernPersonalNewsFromGraph$ = new Subject<void>();
  public reloadGetPersonalNewsFromApi$ = new Subject<void>();
  public reloadGetModernNewsFromSearch$ = new Subject<void>();

  private getSharePointNewsPageFromOriginalUrl$ = new Map<string, Observable<NewsPage>>();

  private renderer: Renderer2; // Used to convert the light page content HTML to plain text for my-news

  private featureSettings = this.featureSettingsStoreService.state;

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private http: HttpClient,
              private httpHelper: HttpHelper,
              private cache: CacheService,
              private spHomeService: SphomeService,
              private featureSettingsStoreService: FeatureSettingsStoreService,
              private searchService: SearchService,
              private licenseService: LicenseService,
              private cultureService: CultureService,
              rendererFactory: RendererFactory2,
              private urlHelperService: UrlHelperService) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  reloadModernTopNewsGraph() {
    this.getModernTopNewsGraph$.clear();
    this.reloadGetModernTopNewsGraph$.next();
  }

  reloadGetTopNewsApi() {
    this.getTopNewsApi$.clear();
    this.reloadGetTopNewsApi$.next();
  }

  reloadAllTopNews() {
    this.getModernTopNewsGraph$.clear();
    this.getTopNewsApi$.clear();
    this.reloadAllTopNews$.next();
  }

  reloadGetModernPersonalNewsFromGraph() {
    this.getModernPersonalNewsFromGraph$ = null;
    this.reloadGetModernPersonalNewsFromGraph$.next();
  }

  reloadGetPersonalNewsFromApi() {
    this.getPersonalNewsFromApi$ = null;
    this.reloadGetPersonalNewsFromApi$.next();
  }

  reloadGetModernNewsFromSearch() {
    this.getModernNewsFromSearch$.clear();
    this.reloadGetModernNewsFromSearch$.next();
  }

  reloadAllPersonalNews() {
    this.getModernPersonalNewsFromGraph$ = null;
    this.getPersonalNewsFromApi$ = null;
    this.getModernNewsFromSearch$.clear();
    this.reloadAllPersonalNews$.next();
  }

  getNewsFromSpHome(start: number = 0, count: number = 10) {

    // Source types for news:
    // Unknown = 0x0,
    // PersonalFeed = 0x1,
    // SuggestedSites = 0x2,
    // FollowedSites = 0x4,
    // PeopleToReadNewsFrom = 0x8,
    // PeopleWorkingWith = 0x10,
    // All = 0x1F
    // e.g. https://westeurope3-sphomep.svc.ms/api/v1/news?start=0&count=200&sourceType=4

    return this.spHomeService.getSPHomeContext()
      .pipe(concatMap(ctx => {
        const httpHeaders = new HttpHeaders().append('SPHome-ApiContext', ctx.Payload);
        return this.http.get<any>(`${ctx.Urls[0]}/api/v1/news?start=${start}&count=${count}`, {
          headers: httpHeaders
        });
      }));
  }

  getNewTopNews(lastCheckedDate: Date, userLanguageTerms$: Observable<Array<UserLanguageTerm>>): Observable<NewsPage[]> {
    return iif(
      () => this.environment.enableWorkplaceLight,
      // Workplace light enabled
      this.licenseService.hasUserAny365License().pipe(
        switchMap(licensed => {
          return iif(
            () => licensed,
            // Workplace light + SharePoint licensed
            forkJoin([this.getNewTopNewsFromApi(lastCheckedDate, userLanguageTerms$), this.getNewOrUpdatedModernTopNewsFromGraph(lastCheckedDate, userLanguageTerms$)]).pipe(
              map(([apiItems, graphItems]) => {
                // Concat the results from sharepoint + api
                return apiItems.concat(graphItems);
              })
            ),
            // Only Workplace Light
            this.getNewTopNewsFromApi(lastCheckedDate, userLanguageTerms$)
          );
        })
      ),
      // Only SharePoint
      this.getNewOrUpdatedModernTopNewsFromGraph(lastCheckedDate, userLanguageTerms$)
    );
  }

  getNewOrUpdatedModernTopNewsFromGraph(lastCheckedDate: Date, userLanguageTerms$: Observable<Array<UserLanguageTerm>>): Observable<NewsPage[]> {
    const newsCenterSite = this.featureSettings.newsCenterSiteUrl ? this.featureSettings.newsCenterSiteUrl : this.environment.coreWorkplaceSiteUrl;
    const listsRequest = `${newsCenterSite}/_api/v2.0/sharePoint/getBaseItemByUrl(url='/SitePages')/items`;
    const httpParams = new HttpParams()
      .append('expand', 'fields(select=Title,BannerImageUrl,BoolField_NewsIsTopNews,BoolField_Language,Description,_CommentCount,_LikeCount,BoolField_SenderTag,PromotedState,FirstPublishedDate,Modified)')
      .append('select', 'fields,id,webUrl')
      .append('filter', `(fields/PromotedState eq 2) and (fields/BoolField_NewsIsTopNews eq 1) and (fields/FirstPublishedDate gt '${lastCheckedDate.toISOString()}')`)
      .append('orderby', 'fields/FirstPublishedDate desc');

    const getNewOrUpdatedModernTopNewsFromGraph$ = this.http
      .get<any>(listsRequest, {
        params: httpParams,
        headers: {
          'Prefer': 'HonorNonIndexedQueriesWarningMayFailRandomly'
        }
      })
      .pipe(
        map((listItemResult) => {
          const listItems = listItemResult.value as ListItem[];
          return listItems.map(item => {
            return new NewsPage({
              Title: item.fields['Title'],
              PublishedDate: item.fields['FirstPublishedDate'],
              Picture: item.fields['BannerImageUrl'] ? item.fields['BannerImageUrl'].Url : null,
              Description: item.fields['Description'],
              CommentsCount: item.fields['_CommentCount'] ? item.fields['_CommentCount'] : 0,
              LikesCount: item.fields['_LikeCount'] ? item.fields['_LikeCount'] : 0,
              Path: item.webUrl,
              LanguageTermId: (item.fields['BoolField_Language'] && item.fields['BoolField_Language'].TermGuid) ? item.fields['BoolField_Language'].TermGuid : '',
              IsSharePointNews: true,
              SenderTags: item.fields['BoolField_SenderTag'] ? item.fields['BoolField_SenderTag'] : null
            });
          });
        }));

    return getNewOrUpdatedModernTopNewsFromGraph$
      .pipe(
        // Language filter toggle
        mergeMap((pages) => {
          return iif(
            () => this.featureSettings.enableLanguageFilteringForSharepointPages,
            userLanguageTerms$.pipe(
              map((userLanguageTerms) => {
                return applyLanguageFilteringSingle(pages, userLanguageTerms, this.featureSettings.enableShowNonLanguageContentForAllUsers, this.featureSettings.defaultLanguageTermId);
              })
            ),
            of(pages)
          );
        }),
        catchError(this.httpHelper.handleError({ operation: 'getNewOrUpdatedModernTopNewsFromGraph', result: new Array<NewsPage>(), rethrow: true }))
      );
  }

  getNewTopNewsFromApi(lastCheckedDate: Date, userLanguageTerms$: Observable<Array<UserLanguageTerm>>): Observable<NewsPage[]> {

    const httpParams = new HttpParams()
      .append('fromDate', lastCheckedDate.toISOString());

    const getNewTopNewsFromApi$ = this.http
      .get<Array<Page>>(`${Constants.API_ENDPOINT}/pages/news/top/new`, {
        params: httpParams,
      })
      .pipe(
        map(pages => {
          return pages.map(page => {
            return new NewsPage({
              Title: page.Title,
              PublishedDate: page.PublishingDate,
              Picture: page.BannerImageUrl,
              Description: page.Content,
              CommentsCount: null,
              LikesCount: null,
              Path: `/pages/news/${page.SlugTitle}-${page.Id}`,
              IsSharePointNews: false,
              LanguageTermId: page.LanguageTermId,
            });
          });
        }));

    return getNewTopNewsFromApi$
      .pipe(
        mergeMap((pages) => {
          // Language filter toggle
          if (this.featureSettings.enableLanguageFilteringForWorkplace) {
            return userLanguageTerms$
              .pipe(
                map((userLanguageTerms) => {
                  return applyLanguageFilteringSingle(pages, userLanguageTerms, this.featureSettings.enableShowNonLanguageContentForAllUsers, this.featureSettings.defaultLanguageTermId);
                })
              );
          } else {
            return of(pages);
          }
        }),
        catchError(this.httpHelper.handleError({ operation: 'getNewTopNewsFromApi', result: new Array<NewsPage>(), rethrow: true }))
      );
  }

  getModernTopNewsFromGraph(amount: number, userLanguageTerms$: Observable<Array<UserLanguageTerm>>): Observable<NewsPage[]> {
    const cacheKey = `${this.cacheKeyPrefix}-${'getModernTopNewsFromGraph'}-${amount.toString()}`;

    if (this.getModernTopNewsGraph$.has(cacheKey)) {
      return this.getModernTopNewsGraph$.get(cacheKey);
    } else {
      const newsCenterSite = this.featureSettings.newsCenterSiteUrl ? this.featureSettings.newsCenterSiteUrl : this.environment.coreWorkplaceSiteUrl;
      const listsRequest = `${newsCenterSite}/_api/v2.0/sharePoint/getBaseItemByUrl(url='/SitePages')/items`;
      const selectFields = `Title,BannerImageUrl,BoolField_NewsIsTopNews,Description,_CommentCount,_LikeCount,BoolField_Channel,BoolField_Language,PromotedState,BoolField_SenderTag,FirstPublishedDate${this.featureSettings.enableModernSPNewsLanguageFilter ? ',' + this.translationFields : ''}`;
      let httpParams = new HttpParams()
        .append('expand', `fields(select=${selectFields})`)
        .append('orderby', 'fields/FirstPublishedDate desc')
        .append('select', 'fields,id,webUrl')
        .append('top', this.featureSettings.enableLanguageFilteringForSharepointPages ? '50' : amount.toString());

      if (this.featureSettings.enableModernSPNewsLanguageFilter) {
        // Filter reads like this: Get only top news where the page has been translated to our language or the apge is not translated and has no translations to our language.
        httpParams = httpParams.append('filter', `(fields/PromotedState eq 2) and (fields/BoolField_NewsIsTopNews eq 1) and ((fields/_SPTranslationLanguage eq '${this.cultureService.getLocaleWithSuffix().toLowerCase()}' and fields/_SPIsTranslation eq 1) or (fields/_SPIsTranslation ne 1 and fields/_SPTranslatedLanguages ne '${this.cultureService.getLocaleWithSuffix().toLowerCase()}'))`);
      } else {
        // Filter reads like this: Get only top news
        httpParams = httpParams.append('filter', `(fields/PromotedState eq 2) and (fields/BoolField_NewsIsTopNews eq 1)`);
      }

      const getModernTopNewsGraph$ = this.http.get<any>(listsRequest, { params: httpParams, headers: { 'Prefer': 'HonorNonIndexedQueriesWarningMayFailRandomly' } })
        .pipe(
          map((listItemResult) => {
            const listItems = listItemResult.value as ListItem[];
            return listItems.map(item => {
              return new NewsPage({
                Title: item.fields['Title'],
                PublishedDate: item.fields['FirstPublishedDate'],
                Picture: item.fields['BannerImageUrl'] ? item.fields['BannerImageUrl'].Url : null,
                Description: item.fields['Description'],
                CommentsCount: item.fields['_CommentCount'] ? item.fields['_CommentCount'] : 0,
                LikesCount: item.fields['_LikeCount'] ? item.fields['_LikeCount'] : 0,
                Channels: item.fields['BoolField_Channel'],
                LanguageTermId: (item.fields['BoolField_Language'] && item.fields['BoolField_Language'].TermGuid) ? item.fields['BoolField_Language'].TermGuid : '',
                Path: item.webUrl,
                IsSharePointNews: true,
                SenderTags: item.fields['BoolField_SenderTag'] ? item.fields['BoolField_SenderTag'] : null
              });
            });
          }));

      this.getModernTopNewsGraph$.set(cacheKey, getModernTopNewsGraph$
        .pipe(
          mergeMap((pages) => {
            // Language filter toggle
            if (this.featureSettings.enableLanguageFilteringForSharepointPages) {
              return userLanguageTerms$
                .pipe(
                  map((userLanguageTerms) => {
                    return applyLanguageFilteringSingle(pages, userLanguageTerms, this.featureSettings.enableShowNonLanguageContentForAllUsers, this.featureSettings.defaultLanguageTermId);
                  })
                );
            } else {
              return of(pages);
            }
          }),
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getModernTopNewsFromGraph', result: new Array<NewsPage>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.reloadGetModernTopNewsGraph$),
          shareReplay({ bufferSize: 1, refCount: true })
        ));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<NewsPage>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getModernTopNewsGraph$.get(cacheKey));
        }
      }
      return this.getModernTopNewsGraph$.get(cacheKey);
    }
  }

  getModernNewsItemInfoFromPagePath(newsPage: NewsPage): Observable<NewsPage> {
    const originalUrl = newsPage.Path;
    const cacheKey = `${this.cacheKeyPrefix}-NewsItemInfo-${originalUrl}`;
    const cachedData = this.cache.get<NewsPage>(cacheKey);
    if (cachedData) {
      return of(cachedData);
    }

    const httpParams = new HttpParams()
        .append('url', `'${originalUrl}'`)
        .append('select', `id,parentReference`);
    const getSharePointNewsPageFromOriginalUrl$ =
        this.http.get<ListItem>(`${this.environment.coreWorkplaceSiteUrl}/_api/v2.0/sharePoint/getBaseItemByUrl`, { params: httpParams })
          .pipe(
            mergeMap(listItem => {
              if (listItem?.id && listItem?.parentReference?.id && listItem?.parentReference?.siteId && listItem?.parentReference['listId']) {
                const authorityUrl = this.urlHelperService.getAuthorityUrlWithoutQueryStrings(originalUrl);
                const hostName = this.urlHelperService.getHostName(originalUrl);
                const url = `${authorityUrl}/_api/v2.1/sites/${hostName},${listItem.parentReference.siteId}/lists/${listItem?.parentReference['listId']}/items/${listItem.id}`;
                return this.getSpPageInfoFromUrl(url);
              } else {
                return of(null);
              }
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
            tap(data => {
              this.useCache && this.cache.set(cacheKey, data, addMinutes(new Date(), 1));
            }),
            );

    return getSharePointNewsPageFromOriginalUrl$;
  }

  getSpPageInfoFromUrl(url: string): Observable<NewsPage> {
    const cacheKey = `${this.cacheKeyPrefix}-${'getPageInfoFromUrl'}-${url}`;
    const cachedData = this.cache.get<NewsPage>(cacheKey);
    if (cachedData) {
      return of(cachedData);
    }

    const httpParams = new HttpParams()
      .append('expand', 'fields(select=Title,BannerImageUrl,BoolField_NewsIsTopNews,BoolField_Language,Description,_CommentCount,_LikeCount,BoolField_SenderTag,PromotedState,FirstPublishedDate,Modified)')
      .append('select', 'fields,id,webUrl');

    const getModernPersonalNewsFromSP$ = this.http
      .get<any>(url, {
        params: httpParams,
        headers: {
          'Prefer': 'HonorNonIndexedQueriesWarningMayFailRandomly'
        }
      })
      .pipe(
        map((listItemResult) => {
           return new NewsPage({
              Title: listItemResult.fields['Title'],
              PublishedDate: listItemResult.fields['FirstPublishedDate'],
              Picture: listItemResult.fields['BannerImageUrl'] ? listItemResult.fields['BannerImageUrl'].Url : null,
              Description: listItemResult.fields['Description'],
              CommentsCount: listItemResult.fields['_CommentCount'] ? listItemResult.fields['_CommentCount'] : 0,
              LikesCount: listItemResult.fields['_LikeCount'] ? listItemResult.fields['_LikeCount'] : 0,
              Path: listItemResult.webUrl,
              LanguageTermId: (listItemResult.fields['BoolField_Language'] && listItemResult.fields['BoolField_Language'].TermGuid) ? listItemResult.fields['BoolField_Language'].TermGuid : '',
              IsSharePointNews: true,
              SenderTags: listItemResult.fields['BoolField_SenderTag'] ? listItemResult.fields['BoolField_SenderTag'] : null
            });
         }),
         shareReplay({ bufferSize: 1, refCount: true }),
         tap(data => {
          this.useCache && this.cache.set(cacheKey, data, addMinutes(new Date(), 1));
       }),
       catchError(this.httpHelper.handleError({ operation: 'getSpPageInfoFromUrl', result: new NewsPage(), cacheKey: cacheKey })));

    return getModernPersonalNewsFromSP$;
   }

  getTopNewsFromApi(amount: number, userLanguageTerms$: Observable<Array<UserLanguageTerm>>): Observable<NewsPage[]> {
    const cacheKey = `${this.cacheKeyPrefix}-${'getTopNewsFromApi'}-${amount.toString()}`;

    if (this.getTopNewsApi$.has(cacheKey)) {
      return this.getTopNewsApi$.get(cacheKey);
    } else {
      const httpParams = new HttpParams()
        .append('amount', this.featureSettings.enableLanguageFilteringForWorkplace ? '50' : amount.toString());

      const httpCall$ = this.http
        .get<Array<Page>>(`${Constants.API_ENDPOINT}/pages/news/top`, {
          params: httpParams,
        });
      const getTopNewsApi$ = httpCall$
        .pipe(
          map((pages) => {
            return pages.map(page => {
              return new NewsPage({
                Title: page.Title,
                PublishedDate: page.PublishingDate,
                Picture: page.BannerImageUrl,
                Description: page.Content,
                CommentsCount: null,
                LikesCount: null,
                Channels: page.ChannelTermPages,
                Path: `/pages/news/${page.SlugTitle}-${page.Id}`,
                IsSharePointNews: false,
                LanguageTermId: page.LanguageTermId
              });
            });
          }));

      this.getTopNewsApi$.set(cacheKey,
        getTopNewsApi$
          .pipe(
            mergeMap((pages) => {
              // Language filter toggle
              if (this.featureSettings.enableLanguageFilteringForWorkplace) {
                return userLanguageTerms$
                  .pipe(
                    map((userLanguageTerms) => {
                      return applyLanguageFilteringSingle(pages, userLanguageTerms, this.featureSettings.enableShowNonLanguageContentForAllUsers, this.featureSettings.defaultLanguageTermId);
                    })
                  );
              } else {
                return of(pages);
              }
            }),
            tap(data => {
              this.useCache && this.cache.set(cacheKey, data);
            }),
            catchError(this.httpHelper.handleError({ operation: 'getTopNewsFromApi', result: new Array<NewsPage>(), rethrow: true, cacheKey: cacheKey })),
            takeUntil(this.reloadGetTopNewsApi$),
            shareReplay({ bufferSize: 1, refCount: true })
          ));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<NewsPage>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getTopNewsApi$.get(cacheKey));
        }
      }
      return this.getTopNewsApi$.get(cacheKey);
    }
  }

  getPersonalNewsFromApi(userChannelTerms$: Observable<Array<UserChannelTerm>>, userLanguageTerms$: Observable<Array<UserLanguageTerm>>, amount: number = 100): Observable<NewsPage[]> {
    const cacheKey = `${this.cacheKeyPrefix}-${'getPersonalNewsFromApi'}-${amount.toString()}`;
    if (this.getPersonalNewsFromApi$) {
      return this.getPersonalNewsFromApi$;
    } else {
      const httpParams = new HttpParams()
        .append('amount', this.featureSettings.enableLanguageFilteringForSharepointPages ? '50' : amount.toString());
      const httpCall$ = this.http
        .get<Array<Page>>(`${Constants.API_ENDPOINT}/pages/news/my`, {
          params: httpParams,
        });

      const pages$ = combineLatest([userChannelTerms$, httpCall$])
        .pipe(
          map(([userChannelTerms, pages]) => {
            return pages.map(page => {
              return new NewsPage({
                Title: page.Title,
                PublishedDate: page.PublishingDate,
                Picture: page.BannerImageUrl,
                Description: this.HTMLToPlainText(page.Content),
                CommentsCount: null,
                LikesCount: null,
                Path: `/pages/news/${page.SlugTitle}-${page.Id}`,
                IsSharePointNews: false,
                Channels: page.ChannelTermPages,
                LanguageTermId: page.LanguageTermId,
              });
            }).filter(i => {
              const channelTerms = i.Channels;
              if (channelTerms && channelTerms.length > 0) {
                const matchingChannels = i.Channels.some(c =>
                  userChannelTerms.some(ct => ct.ChannelTermId === c.ChannelTermId)
                );
                if (matchingChannels) {
                  return i;
                }
              } else {
                return i;
              }
            });
          }),
        );

      this.getPersonalNewsFromApi$ = pages$
        .pipe(
          // Language filter toggle
          mergeMap((pages) => {
            return iif(
              () => this.featureSettings.enableLanguageFilteringForWorkplace,
              userLanguageTerms$.pipe(
                map((userLanguageTerms) => {
                  return applyLanguageFilteringSingle(pages, userLanguageTerms, this.featureSettings.enableShowNonLanguageContentForAllUsers, this.featureSettings.defaultLanguageTermId);
                })
              ),
              of(pages)
            );
          }),
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getPersonalNewsFromApi', result: new Array<NewsPage>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.reloadGetPersonalNewsFromApi$),
          shareReplay({ bufferSize: 1, refCount: true })
        );

      if (this.useCache) {
        const cachedData = this.cache.get<Array<NewsPage>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getPersonalNewsFromApi$);
        }
      }
      return this.getPersonalNewsFromApi$;
    }
  }

  getMyModernPersonalNewsFromGraph(userChannelTerms$: Observable<Array<UserChannelTerm>>, userLanguageTerms$: Observable<Array<UserLanguageTerm>>) {
    if (this.getModernPersonalNewsFromGraph$) {
      return this.getModernPersonalNewsFromGraph$;
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-${'getMyModernPersonalNewsFromGraph'}`;
      const newsCenterSite = this.featureSettings.newsCenterSiteUrl ? this.featureSettings.newsCenterSiteUrl : this.environment.coreWorkplaceSiteUrl;
      const listsRequest = `${newsCenterSite}/_api/v2.0/sharePoint/getBaseItemByUrl(url='/SitePages')/items`;
      const selectFields = `Title,BannerImageUrl,BoolField_Channel,BoolField_Language,BoolField_NewsIsTopNews,Description,_CommentCount,_LikeCount,PromotedState,BoolField_SenderTag,FirstPublishedDate${this.featureSettings.enableModernSPNewsLanguageFilter ? ',' + this.translationFields : ''}`;
      let httpParams = new HttpParams()
        .append('expand', `fields(select=${selectFields})`)
        .append('orderby', 'fields/FirstPublishedDate desc')
        .append('select', 'fields,id,webUrl')
        .append('top', '100');
      if (this.featureSettings.enableModernSPNewsLanguageFilter) {
        // Filter reads like this: Get only non-top news where the page has been translated to our language or the apge is not translated and has no translations to our language.
        httpParams = httpParams.append('filter', `(fields/PromotedState eq 2) and (fields/BoolField_NewsIsTopNews eq 0) and ((fields/_SPTranslationLanguage eq '${this.cultureService.getLocaleWithSuffix().toLowerCase()}' and fields/_SPIsTranslation eq 1) or (fields/_SPIsTranslation ne 1 and fields/_SPTranslatedLanguages ne '${this.cultureService.getLocaleWithSuffix().toLowerCase()}'))`);
      } else {
        // Filter reads like this: Get only non-top top news
        httpParams = httpParams.append('filter', `(fields/PromotedState eq 2) and (fields/BoolField_NewsIsTopNews eq 0)`);
      }

      const httpCall$ = this.http
        .get<any>(listsRequest, {
          params: httpParams,
          headers: {
            'Prefer': 'HonorNonIndexedQueriesWarningMayFailRandomly'
          }
        });
      const getMyModernPersonalNewsFromGraph$ = combineLatest([userChannelTerms$, httpCall$])
        .pipe(
          map(([userChannelTerms, listItemResult]) => {
            const listItems = listItemResult.value as ListItem[];
            return listItems.map(item => {
              return new NewsPage({
                Title: item.fields['Title'],
                PublishedDate: item.fields['FirstPublishedDate'],
                Channels: item.fields['BoolField_Channel'],
                LanguageTermId: (item.fields['BoolField_Language'] && item.fields['BoolField_Language'].TermGuid) ? item.fields['BoolField_Language'].TermGuid : '',
                Picture: item.fields['BannerImageUrl'] ? item.fields['BannerImageUrl'].Url : null,
                Description: item.fields['Description'],
                CommentsCount: item.fields['_CommentCount'] ? item.fields['_CommentCount'] : 0,
                LikesCount: item.fields['_LikeCount'] ? item.fields['_LikeCount'] : 0,
                Path: item.webUrl,
                IsSharePointNews: true,
                SenderTags: item.fields['BoolField_SenderTag'] ? item.fields['BoolField_SenderTag'] : null
              });
            }).filter(i => {
              const channelTerms = i.Channels;
              if (channelTerms && channelTerms.length > 0) {
                let found = true;
                const matchingChannels = i.Channels.filter(c => {
                  found = userChannelTerms.some(ct => ct.ChannelTermId === c.TermGuid);
                  if (found) {
                    return c;
                  }
                });
                if (matchingChannels && matchingChannels.length > 0) {
                  return i;
                }
              } else {
                return i;
              }
            });
          }));

      this.getModernPersonalNewsFromGraph$ = getMyModernPersonalNewsFromGraph$
        .pipe(
          mergeMap((pages) => {
            // Language filter toggle
            return iif(
              () => this.featureSettings.enableLanguageFilteringForSharepointPages,
              userLanguageTerms$.pipe(
                map((userLanguageTerms) => {
                  return applyLanguageFilteringSingle(pages, userLanguageTerms, this.featureSettings.enableShowNonLanguageContentForAllUsers, this.featureSettings.defaultLanguageTermId);
                })
              ),
              of(pages)
            );
          }),
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getMyModernPersonalNewsFromGraph', result: new Array<NewsPage>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.reloadGetModernTopNewsGraph$),
          shareReplay({ bufferSize: 1, refCount: true })
        );

      if (this.useCache) {
        const cachedData = this.cache.get<Array<NewsPage>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getModernPersonalNewsFromGraph$);
        }
      }
      return this.getModernPersonalNewsFromGraph$;
    }
  }

  // Returns moderns news pages from all sites except the Core news center site
  getModernNewsFromAllSitesExceptNewsCenterFromSearch(userLanguageTerms$: Observable<Array<UserLanguageTerm>>, amount: number = 100): Observable<Array<NewsPage>> {
    const newsCenterSite = this.featureSettings.newsCenterSiteUrl ? this.featureSettings.newsCenterSiteUrl : this.environment.coreWorkplaceSiteUrl;
    const excludeSites = this.featureSettings.excludeSitesFromNewsSearch;

    let languageFilterQuery = '';
    if (this.featureSettings.enableModernSPNewsLanguageFilter) {
      languageFilterQuery = ` AND ((SPTranslationLanguage:${this.cultureService.getLocaleWithSuffix().toLowerCase()} AND SPIsTranslation:true) OR (NOT(SPIsTranslation:true) AND NOT(SPTranslatedLanguages:${this.cultureService.getLocaleWithSuffix().toLowerCase()})))`;
    }

    let excludeSitesQuery = '';
    if (excludeSites) {
      excludeSitesQuery = `OR path:${excludeSites.map(s => s).join(' OR path: ')}`;
    }

    const query = `(ContentTypeId: 0x0101009D1CB255DA76424F860D91F20E6C4118* AND PromotedState: 2${languageFilterQuery}) AND NOT(path:${newsCenterSite} ${excludeSitesQuery} )`;

    return this.getModernNewsFromSearch(query, userLanguageTerms$, amount);
  }

  // Returns modern news ...
  //  - from any site (including news center), IF tagged with Core Channel that matches provided channels
  //  - or from News Center site, AND channel field is empty (thus targeted to all) AND top news field is false
  getPersonalModernNewsFromAllSitesFromSearch(userChannelTerms$: Observable<Array<UserChannelTerm>>, userLanguageTerms$: Observable<Array<UserLanguageTerm>>, amount: number = 100): Observable<Array<NewsPage>> {
    return userChannelTerms$
      .pipe(
        switchMap(channels => {
          const channelsMngProp = this.environment.featureSettings.managedPropertyConfig.newsChannelManagedProperty;
          const topNewsMngProp = this.environment.featureSettings.managedPropertyConfig.newsIsTopNewsManagedProperty;
          const excludeSites = this.featureSettings.excludeSitesFromNewsSearch;

          let channelQuery = '';
          if (channels.length > 0) {
            const joinedChannels = channels.map(c => channelsMngProp + ':"GP0|#' + c.ChannelTermId + '"').join(' OR ');
            channelQuery = ` OR ( NOT(${topNewsMngProp}:1) AND (${joinedChannels}) )`;
          }

          // Searching for pages where channel is empty comes from this suggestion: https://fivep.com.au/kierans-tech-talk-searching-for-empty-managed-metadata-columns/
          channelQuery = `( (NOT(${channelsMngProp}:G*) AND ${topNewsMngProp}:0) ${channelQuery} )`;

          let languageFilterQuery = '';
          if (this.featureSettings.enableModernSPNewsLanguageFilter) {
            languageFilterQuery = ` AND ((SPTranslationLanguage:${this.cultureService.getLocaleWithSuffix().toLowerCase()} AND SPIsTranslation:true) OR (NOT(SPIsTranslation:true) AND NOT(SPTranslatedLanguages:${this.cultureService.getLocaleWithSuffix().toLowerCase()})))`;
          }

          let excludeSitesQuery = '';
          if (excludeSites) {
            excludeSitesQuery = ` AND NOT(path:${excludeSites.map(s => s).join(' OR path: ')})`;
          }

          const query = `(ContentTypeId: 0x0101009D1CB255DA76424F860D91F20E6C4118* AND PromotedState: 2${languageFilterQuery}) AND ${channelQuery} ${excludeSitesQuery}`;

          return this.getModernNewsFromSearch(query, userLanguageTerms$, amount);
        })
      );
  }

  // Generic function to fetch modern news using search. Uses POST to allow for large queries.
  getModernNewsFromSearch(query: string, userLanguageTerms$: Observable<Array<UserLanguageTerm>>, amount: number = 100): Observable<Array<NewsPage>> {
    const params = `${amount.toString()}-${query}`;
    if (this.getModernNewsFromSearch$.has(params)) {
      return this.getModernNewsFromSearch$.get(params);
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-${'getModernNewsFromSearch'}-${params}`;
      const postQuery = new SearchPostQuery({
        request: new SearchPostQueryRequest({
          Querytext: query,
          RowLimit: this.featureSettings.enableLanguageFilteringForSharepointPages ? 50 : amount,
          EnableSorting: true,
          TrimDuplicates: false,
          SortList: [
            {
              'Property': 'FirstPublishedDate',
              'Direction': 1
            }
          ],
          SelectProperties: [
            'Title',
            'EditorOwsUser',
            'ModifiedBy',
            'LastModifiedBy',
            'Description',
            'Path',
            'PictureThumbnailURL',
            'LastModifiedTime',
            'FirstPublishedDate',
            'SPWebUrl',
            'SPIsTranslation',
            'SPTranslationLanguage',
            'SPTranslationSourceItemId',
            'SPTranslatedLanguages',
            this.environment.featureSettings.managedPropertyConfig.newsAmountOfCommentsManagedProperty,
            this.environment.featureSettings.managedPropertyConfig.newsAmountOfLikesManagedProperty,
            this.environment.featureSettings.managedPropertyConfig.newsChannelManagedProperty,
            this.environment.featureSettings.managedPropertyConfig.newsIsTopNewsManagedProperty,
            this.environment.featureSettings.managedPropertyConfig.newsLanguageManagedProperty,
            this.environment.featureSettings.managedPropertyConfig.newsSenderTagsManagedProperty,
          ],
          Properties: [
            {
              Name: 'EnableDynamicGroups',
              Value: {
                BoolVal: 'True',
                QueryPropertyValueTypeIndex: 3
              }
            } // include results form private groups - https://www.techmikael.com/2017/01/retrieving-search-results-from-private.html
          ]
        })
      });

      const httpCall$ = this.searchService.sendPostSearchQuery(postQuery);

      const getModernNewsFromSearch$ = httpCall$
        .pipe(
          map(data => {
            const parsedResults = data.map(item => {
              return NewsPage.fromSearchResult(item,
                this.environment.featureSettings.managedPropertyConfig.newsAmountOfCommentsManagedProperty,
                this.environment.featureSettings.managedPropertyConfig.newsAmountOfLikesManagedProperty,
                this.environment.featureSettings.managedPropertyConfig.newsChannelManagedProperty,
                this.environment.featureSettings.managedPropertyConfig.newsLanguageManagedProperty,
                this.environment.featureSettings.managedPropertyConfig.newsSenderTagsManagedProperty);
            });
            return parsedResults;
          }),
          tap((data) => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getModernNewsFromSearch', result: new Array<NewsPage>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.reloadGetModernNewsFromSearch$),
          shareReplay({ bufferSize: 1, refCount: true })
        );

      this.getModernNewsFromSearch$.set(params, getModernNewsFromSearch$
        .pipe(
          mergeMap((pages) => {
            // Language filter toggle
            return iif(
              () => this.featureSettings.enableLanguageFilteringForSharepointPages,
              userLanguageTerms$.pipe(
                map((userLanguageTerms) => {
                  return applyLanguageFilteringSingle(pages, userLanguageTerms, this.featureSettings.enableShowNonLanguageContentForAllUsers, this.featureSettings.defaultLanguageTermId);
                })
              ),
              of(pages)
            );
          }),
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getModernNewsFromSearch', result: new Array<NewsPage>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.reloadGetModernTopNewsGraph$),
          shareReplay({ bufferSize: 1, refCount: true })
        )
      );

      if (this.useCache) {
        const cachedData = this.cache.get<any>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getModernNewsFromSearch$.get(params));
        }
      }

      return this.getModernNewsFromSearch$.get(params);
    }
  }

  HTMLToPlainText(htmlContent: string): string {
    const htmlParser = this.renderer.createElement('div');
    htmlParser.innerHTML = htmlContent;
    const plainText = htmlParser.innerText;
    htmlParser.remove();
    return plainText;
  }
}
